import React, { useEffect, useState } from 'react';
import { HeartOff, Loader2, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import AccountLayout from '../../components/AccountLayout';
import { getImageUrl, getWishlist, removeFromWishlist } from '../../config';

export default function AccountWishlist() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState('');

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        const data = await getWishlist();
        if (!active) return;
        setItems(data.items || []);
      } catch {
        if (active) setItems([]);
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, []);

  const handleRemove = async (productId) => {
    setRemoving(productId);
    try {
      await removeFromWishlist(productId);
      setItems((current) => current.filter((item) => item.product.id !== productId));
    } catch {
      // Keep current list on network errors.
    } finally {
      setRemoving('');
    }
  };

  return (
    <AccountLayout title="Mes favoris">
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 text-secondary animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-sm border border-slate-200 p-10 text-center">
          <HeartOff className="w-10 h-10 text-slate-400 mx-auto mb-4" />
          <h2 className="font-heading text-xl font-semibold text-primary mb-2 uppercase tracking-wider">Aucun favori enregistre</h2>
          <p className="text-sm text-slate-600 mb-5">Ajoutez des produits depuis leur fiche detail pour les retrouver plus tard.</p>
          <Link
            to="/catalogue"
            className="inline-flex items-center gap-2 bg-white text-primary border-2 border-secondary hover:bg-secondary hover:text-white font-bold uppercase tracking-wider rounded-sm px-5 py-2.5 text-sm transition-colors"
          >
            Voir le catalogue
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((item) => {
            const product = item.product;
            return (
              <div key={product.id} className="bg-white rounded-sm border border-slate-200 p-4 flex gap-4">
                <div className="w-28 h-24 rounded-sm overflow-hidden bg-slate-50 shrink-0">
                  {product.images?.[0] ? (
                    <img src={getImageUrl(product.images[0])} alt={product.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs text-slate-500">IMG</div>
                  )}
                </div>

                <div className="flex-1 min-w-0 space-y-3">
                  <div>
                    <Link to={`/produit/${product.id}`} className="font-heading text-lg font-semibold text-primary hover:text-secondary transition-colors">
                      {product.name}
                    </Link>
                    <p className="text-sm text-slate-600 mt-1">{product.price?.toFixed(2)} EUR</p>
                  </div>

                  <div className="flex items-center gap-3">
                    <Link
                      to={`/produit/${product.id}`}
                      className="inline-flex items-center gap-2 bg-white text-primary border-2 border-secondary hover:bg-secondary hover:text-white font-bold uppercase tracking-wider rounded-sm px-4 py-2 text-sm transition-colors"
                    >
                      Voir le produit
                    </Link>
                    <button
                      type="button"
                      onClick={() => handleRemove(product.id)}
                      disabled={removing === product.id}
                      className="inline-flex items-center gap-2 text-rose-600 hover:text-rose-700 text-sm disabled:opacity-60"
                    >
                      {removing === product.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      Retirer
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AccountLayout>
  );
}
